import clsx from 'clsx'
import PropTypes from 'prop-types'
import { useEffect, useRef } from 'react'
import { connect } from 'react-redux'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { differenceInCalendarDays } from 'date-fns'

import SectionHead from '../../../../UI/SectionHead'

import { TCompanies, TRoutes } from '../../../../shared/const'
import { getActivationRequirements } from '../../../../shared/activationRequirements'
import { progressStepForChecklistItem } from '../../../../shared/companyStepLink'

import styles from './style.module.scss'
import { formatVienna } from '../../../../shared/datetime'

/*
 * Was als Nächstes zu tun ist — die erste Spalte des Dashboards.
 *
 * Sie beantwortet die einzige Frage, mit der eine Junior Company auf das
 * Dashboard kommt: „Woran hängt es gerade?" Vorher standen dort fünf Kacheln
 * (Mitarbeiter:innen, weitere Lehrkräfte, Expert:innen, Risikoanalyse,
 * Businessplan). Sie zeigten Namen und Zahlen, aber nicht, was fehlt; was
 * fehlt, stand nur im Fenster hinter „Zur Genehmigung einreichen" — also erst
 * dort, wo man es nicht mehr braucht.
 *
 * Der Ablauf hat zwei Hälften, und die Spalte zeigt immer nur die laufende:
 *
 *  - **vor der Aktivierung** die Voraussetzungen aus
 *    `shared/activationRequirements` — dieselbe Liste, die das Einreichen-
 *    Fenster und die Vorschau der Administration lesen,
 *  - **nach der Aktivierung** die Schritte bis zum Abschluss: Anteilscheine,
 *    Berichte mit ihrer Frist, Rechnungen, Geschäftsbericht.
 *
 * Ob eine Zeile erfüllt ist, entscheidet immer der Server (`activationsStatus`).
 *
 * Dieselbe Spalte sieht die **Lehrkraft** für eine ausgewählte betreute Company
 * — dort mit `readOnly`: keine Fenster, keine Uploads, keine Verweise in die
 * Menüpunkte der Schüler:innen (die ihre Rolle ohnehin nicht öffnen darf).
 *
 * Darüber steht derselbe Ablauf als Balken (UI/CompanyProgress). Welche Zeile
 * zu welchem Knoten gehört, war nur zu erraten — ein Klick auf die Überschrift
 * einer Zeile hebt deshalb den zugehörigen Knoten hervor und umgekehrt
 * (`focusId`/`onFocusStep`, Zuordnung in shared/companyStepLink). Mehrere
 * Zeilen können denselben Knoten meinen: Vier der Voraussetzungen drehen sich
 * um die Mitarbeiter:innen, im Balken steht dafür ein Schritt. Dann leuchten
 * alle vier zugleich auf — genau das ist die Auskunft.
 */

const fmt = wert => (wert ? formatVienna(wert, 'dd.MM.yyyy') : '')

/**
 * Die Frist zu einem Bericht, je nach Unternehmensform. Die Werte pflegt die
 * Administration unter Einstellungen; fehlt einer, bleibt die Zeile ohne Frist
 * stehen (statt „Frist: Invalid Date").
 */
const berichtsFrist = (companyType, reportsDeadline, art) => {
	if (!reportsDeadline) return null

	const wert =
		companyType === TCompanies.JC
			? art === 'annual'
				? reportsDeadline.JC_annual
				: reportsDeadline.JC_semiannual
			: reportsDeadline[companyType]

	return wert ? new Date(wert) : null
}

function CompanySteps(props) {
	const { t } = useTranslation()

	const status = props.activationsStatus || {}
	const istJC = props.companyType === TCompanies.JC
	const reportsDeadline = props.options?.reportsDeadline

	/*
	 * Vor der Aktivierung: die Voraussetzungen. Jede offene Zeile führt dorthin,
	 * wo sie zu erledigen ist — der Businessplan ist die Ausnahme, er wird hier
	 * hochgeladen (einen anderen Ort dafür gibt es nicht).
	 */
	const voraussetzungen = () => {
		// Wo eine Voraussetzung zu erfüllen ist, weiß die Liste selbst
		// (shared/activationRequirements) — eine zweite Zuordnung hier liefe
		// spätestens beim nächsten Punkt auseinander. Ein Punkt bekommt
		// zusätzlich das Fenster, das es dafür schon gibt.
		//
		// Bei den Expert:innen stand hier bis 09.08.2026 das Erfassungsformular.
		// Es gibt nichts mehr zu erfassen: Die Voraussetzung erfüllt, wer eine
		// Expert:in anfragt oder einlädt — beides steht unter „Wirtschafts-
		// experten", und dorthin führt jetzt der Verweis der Zeile.
		const fenster = {
			employees: props.actions?.addEmployee
		}

		const schritte = getActivationRequirements({
			options: props.options,
			activationsStatus: status
		}).map(requirement => {
			const dialog = fenster[requirement.id]

			return {
				id: requirement.id,
				title: requirement.title,
				/*
				 * Ein Punkt, der erst nach der Freischaltung dazugekommen ist,
				 * steht grün da — und der Zusatz sagt, warum. Ohne ihn hielte
				 * ihn jede:r für eine erbrachte Leistung.
				 */
				hint: requirement.grandfathered
					? `${requirement.text} — ${t(
							'This requirement was added after your company was activated and is therefore not required of you.'
					  )}`
					: requirement.text,
				done: requirement.status === true,
				actionLabel: requirement.linkLabel,
				onClick: dialog,
				// Auch dort, wo ein Fenster daneben steht (Mitarbeiter:innen):
				// Der Verweis führt an die Stelle, an der zu sehen ist, was
				// fehlt — das Fenster legt nur gleich eine Person an.
				to: requirement.link
			}
		})

		/*
		 * Der Businessplan ist keine Voraussetzung (die Genehmigung hängt nicht
		 * an ihm), gehört aber in diese Hälfte des Ablaufs: Er entsteht vor der
		 * Aktivierung. Deshalb steht er hier als ausdrücklich freiwilliger
		 * Schritt am Ende — vorher war er eine Kachel wie jede andere und sah
		 * damit aus wie eine Pflicht.
		 */
		schritte.push({
			id: 'businessPlan',
			title: t('Business Plan'),
			hint: t('Optional — you can upload the business plan of your Junior Company here.'),
			optional: true,
			done: !!props.businessPlan,
			file: props.businessPlan,
			upload: props.actions?.uploadBusinessPlan,
			onDelete: props.actions?.deleteBusinessPlan,
			onDownload: props.actions?.downloadBusinessPlan
		})

		return schritte
	}

	/*
	 * Nach der Aktivierung: was bis zum Abschluss des Programms zu tun ist.
	 * Dieselbe Reihenfolge wie im Fortschrittsbalken darüber — eine zweite
	 * Reihenfolge wäre nur verwirrend.
	 */
	const naechsteSchritte = () => {
		const schritte = []

		// Eine Junior Basic Company verkauft keine Anteile.
		if (props.companyType !== TCompanies.JBC) {
			schritte.push({
				id: 'shares',
				title: t('Sell shares'),
				hint: t('Issue share certificates to your shareholders.'),
				done: !!status.shares,
				to: TRoutes.SHARES
			})
		}

		/*
		 * Das Skills4Retail-Konzept, wenn es **den ersten Finanzbericht** sperrt.
		 * Wo es die Aktivierung sperrt, steht es in der ersten Hälfte
		 * (shared/activationRequirements) — beides zugleich gibt es nicht, die
		 * Einstellung kennt je Unternehmensform genau einen der beiden Fälle.
		 *
		 * Bis 25.08.2026 fehlte diese Hälfte ganz: Der Server maß den Punkt,
		 * zeigte ihn aber nirgends. Die Junior Company erfuhr davon, wenn ihr
		 * Bericht abgewiesen wurde — an der einzigen Stelle, an der die Auskunft
		 * nichts mehr nützt. Deshalb steht er hier, **vor** dem Bericht, den er
		 * zurückhält.
		 */
		if (status.conceptAtFirstReport) {
			schritte.push({
				id: 'concept',
				title: t('Skills4Retail concept completed and assessed'),
				hint: t('Required before the first financial report can be submitted'),
				done: status.conceptPassed === true,
				to: TRoutes.CONCEPT,
				actionLabel: t('To the concept')
			})
		}

		schritte.push({
			id: 'reportSemiannual',
			title: istJC ? t('Semi-annual Report') : t('Financial Report'),
			hint: t('Fill in the report and submit it.'),
			done: !!status.reports?.Semiannual,
			deadline: berichtsFrist(props.companyType, reportsDeadline, 'semiannual'),
			to: istJC ? `${TRoutes.REPORTS}?type=semiannual` : TRoutes.REPORTS
		})

		if (istJC) {
			schritte.push({
				id: 'reportAnnual',
				title: t('Annual Report'),
				hint: t('Fill in the report and submit it.'),
				done: !!status.reports?.AnnualReport,
				deadline: berichtsFrist(props.companyType, reportsDeadline, 'annual'),
				to: `${TRoutes.REPORTS}?type=annual`
			})
		}

		schritte.push({
			id: 'invoiceSemiannual',
			title: istJC ? t('Semi-annual Invoice') : t('Invoice'),
			hint: t('The invoice is in your documents as soon as the report has been submitted.'),
			done: !!status.invoices?.Semiannual,
			to: TRoutes.DOCUMENTS
		})

		if (istJC) {
			schritte.push({
				id: 'invoiceAnnual',
				title: t('Annual Invoice'),
				hint: t('The invoice is in your documents as soon as the report has been submitted.'),
				done: !!status.invoices?.AnnualReport,
				to: TRoutes.DOCUMENTS
			})
		}

		/*
		 * Der Geschäftsbericht schließt das Programm ab. Er lässt sich erst
		 * hochladen, wenn die Berichte abgegeben sind — dieselbe Bedingung, die
		 * vorher über der Kachel stand.
		 */
		const berichteAbgegeben = istJC
			? status.reports?.Semiannual && status.reports?.AnnualReport
			: status.reports?.Semiannual || status.reports?.AnnualReport

		schritte.push({
			id: 'finalReport',
			title: t('Business Report'),
			hint: berichteAbgegeben
				? t('Upload your business report.')
				: t('Possible as soon as the financial reports have been submitted.'),
			done: !!status.finalReport,
			locked: !berichteAbgegeben,
			file: props.finalReport,
			upload: berichteAbgegeben ? props.actions?.uploadFinalReport : undefined,
			onDelete: props.actions?.deleteFinalReport,
			onDownload: props.actions?.downloadFinalReport
		})

		/*
		 * Die Urkunde. Sie war bis 25.08.2026 kein Schritt — die Liste endete
		 * beim Geschäftsbericht, obwohl danach noch etwas kommt und der
		 * Geschäftsbericht allein es nicht auslöst.
		 *
		 * Hier gibt es nichts zu tun und nichts anzuklicken: Die Urkunden
		 * entstehen von selbst, sobald der Geschäftsbericht hochgeladen **und**
		 * alle Rechnungen bezahlt sind. Genau das ist die Auskunft, die vorher
		 * fehlte — wer den Bericht abgegeben hat und nichts bekommt, sucht sonst
		 * den Fehler bei sich. Sie liegen danach unter „Dokumente", ein Blatt je
		 * Person.
		 */
		const rechnungenBezahlt = istJC
			? status.invoices?.Semiannual && status.invoices?.AnnualReport
			: status.invoices?.Semiannual
		const urkundeMoeglich = !!status.finalReport && !!rechnungenBezahlt

		schritte.push({
			id: 'certificate',
			title: t('Certificates'),
			hint: urkundeMoeglich
				? t('Your certificates are in your documents.')
				: t('The certificates are issued once the business report is uploaded and all invoices are paid.'),
			done: !!status.certificate,
			locked: !urkundeMoeglich,
			to: TRoutes.DOCUMENTS
		})

		return schritte
	}

	/*
	 * Zu jeder Zeile der Knoten des Fortschrittsbalkens, den sie meint. Ohne
	 * Gegenstück bleibt sie unangetastet — siehe
	 * shared/companyStepLink.
	 */
	const schritte = (props.isActivated ? naechsteSchritte() : voraussetzungen()).map(schritt => ({
		...schritt,
		balkenSchritt: progressStepForChecklistItem(schritt.id, props.isActivated)
	}))

	const pflicht = schritte.filter(schritt => !schritt.optional)
	const erledigt = pflicht.filter(schritt => schritt.done).length

	/*
	 * Die hervorgehobene Zeile ins Bild holen: Der Balken steht über der Liste,
	 * die zugehörige Zeile kann weit unten liegen. `block: 'nearest'` schiebt
	 * nur, was nötig ist — steht sie ohnehin im Bild, bleibt die Seite stehen.
	 */
	const zeilen = useRef({})
	const hervorgehoben = props.focusId ? schritte.find(schritt => schritt.balkenSchritt === props.focusId) : null

	useEffect(() => {
		if (!hervorgehoben) return

		zeilen.current[hervorgehoben.id]?.scrollIntoView({ block: 'nearest' })
		// Nur beim Wechsel des hervorgehobenen Schritts, nicht bei jedem Neubau
		// der Liste.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.focusId])

	const heute = new Date()

	/*
	 * Die Handlung einer Zeile: eine Datei entgegennehmen oder ein Fenster
	 * öffnen. Beides gibt es nur, solange der Schritt offen ist — eine hoch-
	 * geladene Datei noch einmal hochzuladen ergibt keinen Sinn.
	 */
	const aktion = schritt => {
		if (props.readOnly || schritt.done) return null

		if (schritt.upload) {
			return (
				<label className={clsx(styles.action, styles.actionUpload)}>
					<input
						type="file"
						accept="application/pdf"
						className={styles.file}
						disabled={props.isLoading}
						onChange={schritt.upload}
					/>
					{t('Upload')}
				</label>
			)
		}

		if (schritt.onClick) {
			return (
				<button type="button" className={styles.action} onClick={schritt.onClick}>
					{t('Add')}
				</button>
			)
		}

		return null
	}

	/*
	 * Der Weg dorthin, wo der Schritt hingehört — „Zu den Mitarbeiter:innen",
	 * „Zur Risikoanalyse", „Öffnen".
	 *
	 * Er stand bis 21.08.2026 als Knopf in der Zeile und **nur an offenen
	 * Schritten**. Damit fehlte er genau dort, wo am häufigsten nachgesehen
	 * wird: Wer wissen will, wer schon eine Einverständniserklärung abgegeben
	 * hat, sucht die Stelle auch dann, wenn der Haken längst steht. Deshalb
	 * jetzt an jeder Zeile — und als ruhige Textzeile, nicht als Knopf: Neun
	 * gleich laute Knöpfe untereinander sagen nichts mehr darüber, worauf es
	 * gerade ankommt.
	 *
	 * In der Ansicht der Lehrkraft (`readOnly`) bleibt er weg: Die Menüpunkte
	 * der Schüler:innen kann sie ohnehin nicht öffnen.
	 */
	const verweis = schritt => {
		if (props.readOnly) return null

		const pfeil = (
			<span className={styles.linkArrow} aria-hidden="true">
				→
			</span>
		)

		if (schritt.href) {
			return (
				<a className={styles.link} href={schritt.href} target="_blank" rel="noreferrer">
					{schritt.actionLabel || t('Open link')}
					{pfeil}
				</a>
			)
		}

		if (schritt.to) {
			return (
				<Link className={styles.link} to={schritt.to}>
					{schritt.actionLabel || t('Go there')}
					{pfeil}
				</Link>
			)
		}

		return null
	}

	return (
		<section className={clsx(styles.root, props.className)}>
			<SectionHead
				className={styles.header}
				title={props.isActivated ? t('Checklist up to the certificate') : t('Checklist up to activation')}
			/>

			<div className={styles.progress}>
				<div className={styles.bar}>
					<span
						className={styles.barFill}
						style={{ width: `${pflicht.length ? (erledigt / pflicht.length) * 100 : 0}%` }}
					/>
				</div>
				<span className={styles.progressText}>
					{erledigt} {t('of')} {pflicht.length} {t('done')}
				</span>
			</div>

			{/* Eingereicht heißt: Es liegt nicht mehr an der Junior Company. Ohne
			    diesen Satz liest sich eine noch offene Zeile wie ein Versäumnis. */}
			{!props.isActivated && props.isNeedReview && (
				<p className={styles.note}>{t('Company submitted for approval')}</p>
			)}

			<ol className={styles.list}>
				{schritte.map(schritt => {
					const ueberfaellig =
						!schritt.done && schritt.deadline && differenceInCalendarDays(heute, schritt.deadline) > 0
					const tage = schritt.deadline ? Math.abs(differenceInCalendarDays(heute, schritt.deadline)) : 0
					const handlung = aktion(schritt)
					const weg = verweis(schritt)

					// Anklickbar ist eine Zeile nur, wenn es den Knoten im Balken
					// wirklich gibt und jemand zuhört (auf dem Dashboard tun das
					// beide Spalten gemeinsam, siehe shared/companyStepLink).
					const verknuepft = !!props.onFocusStep && !!schritt.balkenSchritt
					const leuchtet = verknuepft && props.focusId === schritt.balkenSchritt

					return (
						<li
							key={schritt.id}
							ref={element => {
								zeilen.current[schritt.id] = element
							}}
							className={clsx(
								styles.item,
								schritt.done && styles.itemDone,
								schritt.locked && styles.itemLocked,
								leuchtet && styles.itemFocused
							)}
						>
							<span className={styles.mark} aria-hidden="true">
								{schritt.done ? '✓' : ''}
							</span>

							<div className={styles.body}>
								<h3 className={styles.title}>
									{/* Nur die Überschrift ist der Knopf, nicht die ganze
									    Karte: In ihr stehen schon Verweise, Uploads und
									    Dateinamen — ein Knopf um sie herum verschlänge
									    deren Klicks. */}
									{verknuepft ? (
										<button
											type="button"
											className={styles.titleButton}
											title={t('Show the matching step in the progress bar')}
											aria-pressed={leuchtet}
											onClick={() => props.onFocusStep(schritt.balkenSchritt)}
										>
											{schritt.title}
										</button>
									) : (
										schritt.title
									)}
									{schritt.optional && <span className={styles.optional}>{t('optional')}</span>}
								</h3>
								{!!schritt.hint && <p className={styles.hint}>{schritt.hint}</p>}

								{/* Die Datei steht unter der Zeile, nicht statt ihr: Der Schritt
								    bleibt lesbar, auch wenn er erledigt ist. */}
								{!!schritt.file?.originalName && (
									<p className={styles.fileRow}>
										<button
											type="button"
											className={styles.fileName}
											onClick={() => schritt.onDownload?.(schritt.file)}
											disabled={!schritt.onDownload}
										>
											{schritt.file.originalName}
										</button>
										{!props.readOnly && !!schritt.onDelete && (
											<button
												type="button"
												className={styles.fileDelete}
												onClick={schritt.onDelete}
											>
												{t('Remove')}
											</button>
										)}
									</p>
								)}

								{!!schritt.deadline && (
									<p className={clsx(styles.deadline, ueberfaellig && styles.deadlineOverdue)}>
										{t('Deadline')}: {fmt(schritt.deadline)}
										{/* Ein Schlüssel mit Platzhalter statt drei Wortstücken:
										    „überfällig seit 69 Tagen" ist im Deutschen ein Dativ,
										    zusammengesetzt käme „69 Tage" heraus. */}
										{ueberfaellig && ` · ${t('overdue by {{days}} days', { days: tage })}`}
									</p>
								)}

								{/* Handlung und Verweis stehen in einer Zeile, damit die Karte
								    nicht bei jedem Schritt unterschiedlich hoch wird. */}
								{(!!handlung || !!weg) && (
									<div className={styles.actions}>
										{handlung}
										{weg}
									</div>
								)}
							</div>
						</li>
					)
				})}
			</ol>
		</section>
	)
}

CompanySteps.propTypes = {
	activationsStatus: PropTypes.object,
	companyType: PropTypes.string,
	isActivated: PropTypes.bool,
	isNeedReview: PropTypes.bool,
	businessPlan: PropTypes.object,
	finalReport: PropTypes.object,
	// Ohne Handlungen (Lehrkraft): nur lesen.
	readOnly: PropTypes.bool,
	// Verknüpfung mit dem Fortschrittsbalken darüber (siehe
	// shared/companyStepLink): welcher Schritt gerade hervorgehoben ist und was
	// ein Klick meldet. Fehlen sie, ist die Liste wie bisher nur zu lesen.
	focusId: PropTypes.string,
	onFocusStep: PropTypes.func,
	isLoading: PropTypes.bool,
	actions: PropTypes.object,
	className: PropTypes.string,
	options: PropTypes.object
}

const mapStateToProps = state => ({
	options: state.options
})

export default connect(mapStateToProps)(CompanySteps)
